<script lang="ts" setup>
import {
  MedicalSystemRtSystemDto,
  MedicalSystemSetupBaseDto,
  TaskTypeDescription,
} from '@/api/generator';
import Notification from '@/components/notifications/Notification.vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import { computed, ref, toRef, watch } from 'vue';
import EntityScopeSelectionModal from './EntityScopeSelectionModal.vue';
import { useField } from 'vee-validate';
import { api } from '@/api';
import { toCreateOrUpdateMedicalSystemSetupBaseDto } from '../mapping';
import { useToggle } from '@vueuse/core';

interface EntityScopeSelectionProps {
  name: string;
  taskType: TaskTypeDescription;
  options: Array<MedicalSystemRtSystemDto>;
  excludedIds?: string[];
}

const props = defineProps<EntityScopeSelectionProps>();

const previousMedicalSystem = ref<string | undefined | null>();

const systemHasChanged = ref<boolean>(false);

const { value: medicalSystemSetup, setValue: setMedicalSystem } = useField<
  MedicalSystemSetupBaseDto | undefined
>(toRef(() => props.name));

const targetItem = computed(() =>
  props.options.find((item) => item.id === medicalSystemSetup.value?.targetSystemEntityId),
);

async function update(targetSystemEntityId: string | undefined) {
  previousMedicalSystem.value = targetItem.value?.id;

  if (targetSystemEntityId) {
    const { result } = await api.taskItemsApi.getMedicalSystemSetupTemplate({
      getMedicalSystemSetupTemplate: {
        targetSystemId: targetSystemEntityId,
        taskType: props.taskType,
        previousMedicalSystemSetup: medicalSystemSetup.value
          ? toCreateOrUpdateMedicalSystemSetupBaseDto(medicalSystemSetup.value)
          : null,
      },
    });

    if (result) {
      setMedicalSystem(result);
    }
  } else {
    setMedicalSystem(undefined);
  }
}

const [modalIsOpen, toggleModal] = useToggle(false);

function closeDialogAndEmitId(newId: string) {
  modalIsOpen.value = false;
  update(newId);
}

// serial number removed from tile
const filteredOptions = computed(() =>
  props.options
    .filter((modifiedSystemOption) => !props.excludedIds?.includes(modifiedSystemOption.id))
    .map((item) => {
      return { ...item, serialNumber: undefined };
    }),
);

watch(
  () => targetItem.value?.id,
  (id) => {
    systemHasChanged.value =
      (previousMedicalSystem.value && id !== previousMedicalSystem.value) || false;
  },
);

defineExpose({
  toggleModal,
});
</script>

<template>
  <div>
    <Notification
      v-if="systemHasChanged"
      :value="{
        type: 'warning',
        message: $t('MedicalSystemChangedNotification'),
      }"
      class="mb-6 pa-4"
      @click:close="systemHasChanged = false"
    />

    <div class="grid">
      <RTSystemTile
        v-if="targetItem"
        class="fit-content"
        :item="{ ...targetItem, serialNumber: undefined }"
      />
    </div>

    <EntityScopeSelectionModal
      v-model="modalIsOpen"
      :title="$t('AddMedicalSystem')"
      :message="$t('AddMedicalSystemToTaskMsg')"
      :selectedId="targetItem?.id"
      :options="filteredOptions"
      :notification="
        targetItem?.id
          ? {
              type: 'warning',
              message: $t('SelectNewMedicalSystemNotification'),
            }
          : undefined
      "
    >
      <template #innerActions="{ selectedItem }">
        <SecondaryVBtn
          :disabled="!selectedItem"
          @click="selectedItem && closeDialogAndEmitId(selectedItem)"
        >
          {{ $t('Apply') }}
        </SecondaryVBtn>
      </template>
    </EntityScopeSelectionModal>
  </div>
</template>

<style lang="scss" scoped>
.scope-title {
  padding-bottom: 20px !important;
}
</style>
